import { apiRequest } from "@/shared/lib/apiClient";

/* Référentiels de codification : codes actes et codes d'affection (CIM-10). */

export type Option = { value: string; label: string };

export type ActCode = {
  code: string;
  name: string;
  category: string | null;
  kind: "act" | "refaction" | "fee";
  kind_label: string;
  care_category: string | null;
  care_category_label: string | null;
  /** Nature d'actes du reporting. */
  act_group: string | null;
  act_group_label: string | null;
  active: boolean;
  /** Nombre de lignes de facture qui utilisent ce code. */
  used: number;
};

export type ActCodesResponse = {
  acts: ActCode[];
  kinds: Option[];
  care_categories: Option[];
  act_groups: Option[];
  can_edit: boolean;
};

export type ActCodePayload = {
  code?: string;
  name: string;
  category: string | null;
  kind: string;
  care_category: string | null;
  act_group: string | null;
  active: boolean;
};

export type AffectionCode = {
  code: string;
  label: string;
  chapter: string | null;
  active: boolean;
  /** Liste internationale OMS. */
  who_list: boolean;
  /** Nombre de patients de factures codés avec cette affection. */
  used: number;
};

export type Icd10Chapter = { chapter: string; range: string; label: string };

export type AffectionCodesResponse = {
  codes: AffectionCode[];
  total: number;
  page: number;
  per_page: number;
  chapters: Icd10Chapter[];
  can_edit: boolean;
};

export type AffectionCodePayload = { code?: string; label: string; chapter: string | null; active: boolean };

export type ImportResult = { created: number; updated: number; errors: string[] };

function query(params: Record<string, string | number | undefined>): string {
  const search = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== "") search.set(key, String(value));
  });
  const text = search.toString();
  return text ? `?${text}` : "";
}

export async function fetchActCodes(filters: { q?: string; kind?: string; active?: string } = {}): Promise<ActCodesResponse> {
  return apiRequest<ActCodesResponse>(`/referentials/acts${query(filters)}`, { auth: true });
}

export async function saveActCode(payload: ActCodePayload, code?: string): Promise<ActCode> {
  const response = await apiRequest<{ act: ActCode }>(
    code ? `/referentials/acts/${encodeURIComponent(code)}` : "/referentials/acts",
    { method: code ? "PUT" : "POST", auth: true, body: payload },
  );
  return response.act;
}

export async function fetchAffectionCodes(filters: {
  q?: string;
  chapter?: string;
  active?: string;
  page?: number;
}): Promise<AffectionCodesResponse> {
  return apiRequest<AffectionCodesResponse>(`/referentials/icd10${query({ ...filters, per_page: 50 })}`, { auth: true });
}

export async function saveAffectionCode(payload: AffectionCodePayload, code?: string): Promise<AffectionCode> {
  const response = await apiRequest<{ code: AffectionCode }>(
    code ? `/referentials/icd10/${encodeURIComponent(code)}` : "/referentials/icd10",
    { method: code ? "PUT" : "POST", auth: true, body: payload },
  );
  return response.code;
}

export async function importReferential(kind: "acts" | "icd10", file: File): Promise<ImportResult> {
  const form = new FormData();
  form.append("file", file);
  const response = await apiRequest<{ result: ImportResult }>(`/referentials/${kind}/import`, {
    method: "POST",
    auth: true,
    body: form,
  });
  return response.result;
}

/** Fichier CSV (séparateur « ; », lisible par Excel) téléchargé depuis le navigateur. */
export function downloadCsv(filename: string, header: string[], rows: (string | number | null)[][]): void {
  const cell = (value: string | number | null) => {
    const text = value === null ? "" : String(value);
    return /[";\n]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
  };
  const content = [header, ...rows].map((row) => row.map(cell).join(";")).join("\n");
  const url = URL.createObjectURL(new Blob([`﻿${content}`], { type: "text/csv;charset=utf-8" }));
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  link.click();
  URL.revokeObjectURL(url);
}
